// @ts-check

/**
 * @file Renders a shared task summary card used across task listings.
 */

import _ from "gettext-universal/build/src/translate.js"
import {memo} from "react"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import useBreakpoint from "responsive-breakpoints/build/use-breakpoint.js"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"

import buildRoutePath from "../shared/build-route-path"
import Button from "./button"
import Card from "./card"
import Link from "./link"
import {translatedTaskState} from "../shared/task-state"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"

/**
 * @typedef {object} Task
 * @property {() => boolean} [canClose] Remote-only command capability.
 * @property {() => Date | null} createdAt Creation timestamp.
 * @property {() => string | number} id Durable identifier.
 * @property {() => string | null} name Display name.
 * @property {() => number | null} priority Priority value.
 * @property {() => Project | null} [project] Loaded remote relationship.
 * @property {() => string | null} projectId Owning project identifier.
 * @property {(name: string) => number} [readCount] Selected remote count accessor.
 * @property {() => string | null} state State value.
 */
/** @typedef {import("../frontend-models/project").default | import("../models/project.js").default} Project */

/** @import {BoardThemeColors} from "../shared/theme" */
/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * Resolves background + text tokens for a priority level. The badge
 * keeps its semantic — high stays red, low stays green, medium stays
 * amber — and just rebuilds against the active palette so the badge
 * is legible in both light and dark mode.
 * @param {"high" | "medium" | "low"} level Bucketed priority — high (P0/P1), medium (default), or low (P5+).
 * @param {BoardThemeColors} colors Palette returned from `useThemeColors`.
 * @returns {{background: string, text: string}} Background fill and text/icon color for the badge pill.
 */
function priorityBadgeColors(level, colors) {
  if (level === "high") {
    return {background: colors.dangerBackground, text: colors.dangerText}
  }
  if (level === "low") {
    return {background: colors.successBackground, text: colors.successText}
  }
  return {background: colors.warningBackground, text: colors.warningText}
}

/**
 * @typedef {object} TaskCardProps
 * @property {string} [cardTestID] Override for the root card test id. Defaults to `${testIDPrefix}/task-${task.id()}/card`.
 * @property {number} [assignedUsersCount] Local assigned-user count. Remote cards fall back to their selected count.
 * @property {boolean} [canClose] Whether the close action is available. Remote cards fall back to their command contract.
 * @property {boolean} [commandsDisabled] Whether online-only task commands are disabled.
 * @property {number} [commentsCount] Local comment count. Remote cards fall back to their selected count.
 * @property {(task: Task) => void} [onClosePress] Optional close-press callback. When omitted the close button is hidden.
 * @property {(task: Task) => void} [onDeletePress] Optional delete-press callback. When omitted the delete button is hidden.
 * @property {Record<string, string | string[] | undefined>} params Current route params (forwarded so links keep system-test query params).
 * @property {Project | null} [project] Project display record for local tasks. Remote cards fall back to the loaded relationship.
 * @property {boolean} [showCreatedAt] Whether to show the created-at date on desktop widths.
 * @property {Task} task Task to render.
 * @property {string} testIDPrefix Prefix used for every test id emitted by the card (e.g. `tasksIndexScreen`).
 * @property {number} [timelogsCount] Local timelog count. Remote cards fall back to their selected count.
 */

/** @typedef {Record<string, never>} TaskCardState */

/**
 * Shared task summary card. Used by the frontpage, tasks index, project
 * show page, and other list surfaces that need the same responsive task
 * layout.
 * @augments {ShapeComponent<TaskCardProps, TaskCardState>}
 */
class TaskCard extends ShapeComponent {
  static propTypes = propTypesExact({
    assignedUsersCount: PropTypes.number,
    canClose: PropTypes.bool,
    cardTestID: PropTypes.string,
    commandsDisabled: PropTypes.bool,
    commentsCount: PropTypes.number,
    onClosePress: PropTypes.func,
    onDeletePress: PropTypes.func,
    params: PropTypes.object.isRequired,
    project: PropTypes.object,
    showCreatedAt: PropTypes.bool,
    task: PropTypes.object.isRequired,
    testIDPrefix: PropTypes.string.isRequired,
    timelogsCount: PropTypes.number
  })

  static defaultProps = {
    assignedUsersCount: undefined,
    canClose: undefined,
    cardTestID: undefined,
    commandsDisabled: false,
    commentsCount: undefined,
    onClosePress: undefined,
    onDeletePress: undefined,
    project: undefined,
    showCreatedAt: false,
    timelogsCount: undefined
  }

  /** @type {ReturnType<typeof useBreakpoint>} */
  breakpoint
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires viewport breakpoints and the active theme palette into the instance. */
  setup() {
    this.breakpoint = useBreakpoint()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the task card.
   * @returns {ReactNode} Card content.
   */
  render() {
    const {cardTestID, onClosePress, onDeletePress, params, showCreatedAt, task, testIDPrefix} = this.p
    const taskId = task.id()
    const project = this.p.project === undefined ? requiredProject(task) : this.p.project
    const priority = task.priority()
    const state = task.state() || "open"
    const createdAtLabel = formatDate(task.createdAt())
    const timelogsCount = this.p.timelogsCount ?? requiredCount(task, "timelogsCount")
    const commentsCount = this.p.commentsCount ?? requiredCount(task, "commentsCount")
    const assignedUsersCount = this.p.assignedUsersCount ?? requiredCount(task, "assignedUsersCount")
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    const hasCounts = timelogsCount > 0 || commentsCount > 0 || assignedUsersCount > 0
    return (
      <Card
        style={styles.taskCardSpacing ||= {
          marginBottom: 12
        }}
        testID={cardTestID || `${testIDPrefix}/task-${taskId}/card`}
      >
        <View
          style={styles.taskCardHeader ||= {
            alignItems: "flex-start",
            flexDirection: "row",
            gap: 8,
            justifyContent: "space-between",
            marginBottom: 6
          }}
          testID={`${testIDPrefix}/task-${taskId}/header`}
        >
          <Link
            href={buildRoutePath(params, taskPath(task, taskId))}
            style={styles.taskNameLink ||= {
              flex: 1
            }}
            testID={`${testIDPrefix}/task-${taskId}/link`}
          >
            <Text
              style={styles[`taskName-${themeName}`] ||= {
                fontSize: 16,
                fontWeight: 700
              }}
              testID={`${testIDPrefix}/task-${taskId}/name`}
            >
              {task.name() || _("Untitled task")}
            </Text>
          </Link>
          {showCreatedAt && this.tt.breakpoint.mdUp && createdAtLabel !== "" &&
            <Text
              style={styles[`taskCreatedAt-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 13,
                fontWeight: 500
              }}
              testID={`${testIDPrefix}/task-${taskId}/createdAt`}
            >
              {createdAtLabel}
            </Text>
          }
        </View>
        {project &&
          <Link
            href={buildRoutePath(params, `/projects/${project.id()}`)}
            style={styles[`taskProject-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 13
            }}
            testID={`${testIDPrefix}/task-${taskId}/project`}
          >
            {project.name() || "-"}
          </Link>
        }
        <View
          style={styles[`taskCardFooter-${this.tt.breakpoint.mdUp}`] ||= this.tt.breakpoint.styling({
            base: {
              alignItems: "flex-start",
              gap: 12,
              marginTop: 10
            },
            mdUp: {
              alignItems: "center",
              flexDirection: "row",
              gap: 12,
              justifyContent: "space-between",
              marginTop: 10
            }
          })}
          testID={`${testIDPrefix}/task-${taskId}/footer`}
        >
          <View
            style={styles.taskMetadata ||= {
              alignItems: "center",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 8
            }}
            testID={`${testIDPrefix}/task-${taskId}/metadata`}
          >
            {priority != null && renderPriorityBadge(priority, taskId, testIDPrefix, colors, themeName)}
            {renderStateBadge(state, taskId, testIDPrefix, colors, themeName)}
            {hasCounts &&
              <View
                style={styles.taskCounts ||= {
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 12
                }}
                testID={`${testIDPrefix}/task-${taskId}/counts`}
              >
                {timelogsCount > 0 &&
                  renderCountText({
                    label: _("%{count} timelogs", {
                      count: String(timelogsCount)
                    }),
                    taskId,
                    testIDPrefix,
                    type: "timelogs",
                    colors,
                    themeName
                  })
                }
                {commentsCount > 0 &&
                  renderCountText({
                    label: _("%{count} comments", {
                      count: String(commentsCount)
                    }),
                    taskId,
                    testIDPrefix,
                    type: "comments",
                    colors,
                    themeName
                  })
                }
                {assignedUsersCount > 0 &&
                  renderCountText({
                    label: _("%{count} assigned", {
                      count: String(assignedUsersCount)
                    }),
                    taskId,
                    testIDPrefix,
                    type: "assigned",
                    colors,
                    themeName
                  })
                }
              </View>
            }
          </View>
          <View
            style={styles.taskActions ||= {
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 8
            }}
            testID={`${testIDPrefix}/task-${taskId}/actions`}
          >
            {onClosePress && (this.p.canClose ?? requiredCanClose(task)) &&
              <Button
                disabled={this.p.commandsDisabled}
                iconName="circle-xmark"
                label={_("Close")}
                onPress={this.tt.onClosePress}
                testID={`${testIDPrefix}/task-${taskId}/closeButton`}
                variant="neutral"
              />
            }
            <Button
              disabled={this.p.commandsDisabled}
              href={buildRoutePath(params, taskPath(task, taskId, "/edit"))}
              iconName="pen-to-square"
              label={_("Edit")}
              testID={`${testIDPrefix}/task-${taskId}/editButton`}
            />
            {onDeletePress &&
              <Button
                disabled={this.p.commandsDisabled}
                iconName="trash"
                label={_("Delete")}
                onPress={this.tt.onDeletePress}
                testID={`${testIDPrefix}/task-${taskId}/deleteButton`}
                variant="destructive-outline"
              />
            }
          </View>
        </View>
      </Card>
    )
  }

  /**
   * Sends the card task to the optional close callback.
   * @throws {Error} When the close handler is missing.
   */
  onClosePress = () => {
    if (!this.p.onClosePress) throw new Error("Expected onClosePress before closing a task")
    this.p.onClosePress(this.p.task)
  }

  /**
   * Sends the card task to the optional delete callback.
   * @throws {Error} When the delete handler is missing.
   */
  onDeletePress = () => {
    if (!this.p.onDeletePress) throw new Error("Expected onDeletePress before deleting a task")
    this.p.onDeletePress(this.p.task)
  }
}

/**
 * Formats a task creation date for display.
 * @param {Date | null} value Stored creation date.
 * @returns {string} Localized date text or empty string.
 */
function formatDate(value) {
  if (!value) return ""
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  return date.toLocaleDateString()
}

/**
 * Renders a task count label.
 * @param {{colors: BoardThemeColors, label: string, taskId: string | number, testIDPrefix: string, themeName: string, type: string}} args Count rendering options.
 * @returns {ReactNode} Count label.
 */
function renderCountText({colors, label, taskId, testIDPrefix, themeName, type}) {
  return (
    <Text
      style={styles[`taskCount-${themeName}-${type}`] ||= {
        color: colors.textMuted,
        fontSize: 12,
        fontWeight: 600
      }}
      testID={`${testIDPrefix}/task-${taskId}/counts/${type}`}
    >
      {label}
    </Text>
  )
}

/**
 * Renders a priority badge for a task card.
 * @param {number} priority Task priority value.
 * @param {string | number} taskId Task identifier used for test ids.
 * @param {string} testIDPrefix Prefix used for the badge test id.
 * @param {BoardThemeColors} colors Palette returned from `useThemeColors`.
 * @param {string} themeName Theme identifier used to key cached styles per theme.
 * @returns {ReactNode} Badge showing the task priority.
 */
function renderPriorityBadge(priority, taskId, testIDPrefix, colors, themeName) {
  const label = `P${priority}`
  /** @type {"high" | "medium" | "low"} */
  let level = "medium"
  if (priority <= 1) level = "high"
  else if (priority >= 5) level = "low"
  const badgeColors = priorityBadgeColors(level, colors)
  return (
    <View
      style={styles[`priorityBadge-${themeName}-${level}`] ||= {
        alignItems: "center",
        alignSelf: "center",
        backgroundColor: badgeColors.background,
        borderRadius: 999,
        justifyContent: "center",
        minHeight: 20,
        paddingHorizontal: 8,
        paddingVertical: 2
      }}
      testID={`${testIDPrefix}/task-${taskId}/priority`}
    >
      <Text
        style={styles[`priorityBadgeText-${themeName}-${level}`] ||= {
          color: badgeColors.text,
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 14
        }}
        testID={`${testIDPrefix}/task-${taskId}/priority/text`}
      >
        {label}
      </Text>
    </View>
  )
}

/**
 * Renders a state badge for a task card.
 * @param {string} state Task state value.
 * @param {string | number} taskId Task identifier used for test ids.
 * @param {string} testIDPrefix Prefix used for the badge test id.
 * @param {BoardThemeColors} colors Palette returned from `useThemeColors`.
 * @param {string} themeName Theme identifier used to key cached styles per theme.
 * @returns {ReactNode} Badge showing the task state.
 */
function renderStateBadge(state, taskId, testIDPrefix, colors, themeName) {
  const isOpen = state === "open"
  return (
    <View
      style={styles[`stateBadge-${themeName}-${isOpen}`] ||= {
        alignItems: "center",
        alignSelf: "center",
        backgroundColor: isOpen ? colors.successBackground : colors.surfaceMuted,
        borderRadius: 999,
        justifyContent: "center",
        minHeight: 20,
        paddingHorizontal: 8,
        paddingVertical: 2
      }}
      testID={`${testIDPrefix}/task-${taskId}/state`}
    >
      <Text
        style={styles[`stateBadgeText-${themeName}-${isOpen}`] ||= {
          color: isOpen ? colors.successText : colors.textMuted,
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 14
        }}
        testID={`${testIDPrefix}/task-${taskId}/state/text`}
      >
        {translatedTaskState(state)}
      </Text>
    </View>
  )
}

/** @type {NamedExoticComponent<TaskCardProps>} */
const TaskCardComponent = memo(shapeComponent(TaskCard))

export default TaskCardComponent

/**
 * Carries immutable project identity into task routes that cannot derive it from the pathname.
 * @param {Task} task Task record.
 * @param {string | number} taskId Task identifier.
 * @param {string} [suffix] Optional nested task route suffix.
 * @returns {string} Project-qualified task route.
 */
function taskPath(task, taskId, suffix = "") {
  const projectId = task.projectId()
  const path = `/tasks/${taskId}${suffix}`

  return projectId ? `${path}?projectId=${encodeURIComponent(projectId)}` : path
}

/**
 * Reads the remote-only project relationship when no local display record was supplied.
 * @param {Task} task Task record.
 * @returns {Project | null} Loaded remote project relationship.
 * @throws {Error} When a local task has no explicit project.
 */
function requiredProject(task) {
  if (!task.project) throw new Error("TaskCard requires an explicit project for local tasks")
  return task.project()
}

/**
 * Reads one selected remote count when no local count was supplied.
 * @param {Task} task Task record.
 * @param {string} name Selected count name.
 * @returns {number} Selected relationship count.
 * @throws {Error} When a local task has no explicit count.
 */
function requiredCount(task, name) {
  if (!task.readCount) throw new Error(`TaskCard requires an explicit ${name} for local tasks`)
  return task.readCount(name)
}

/**
 * Reads the remote close capability when no local capability was supplied.
 * @param {Task} task Task record.
 * @returns {boolean} Whether the task can be closed.
 * @throws {Error} When a local task has no explicit close capability.
 */
function requiredCanClose(task) {
  if (!task.canClose) throw new Error("TaskCard requires an explicit canClose value for local tasks")
  return task.canClose()
}
